import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Download, LayoutGrid, MessageSquare, Rows3, RotateCcw } from 'lucide-react';
import type { BannerCard, BannerKind, CampaignDetail } from '../../shared/types';
import { post, qk, withShare } from '../lib/api';
import {
  filterBanners,
  groupBanners,
  useStoredState,
  type GroupBy,
  type StatusFilter,
} from '../lib/collection';
import { formatBytes, formatDate, formatName, KIND_LABELS, pluralize } from '../lib/format';
import { BannerFrame, sizeOf, useElementSize } from './BannerMedia';
import { CollectionBar } from './CollectionBar';
import { CommentsOverview } from './CommentsOverview';
import { useGuest } from './GuestProvider';
import { errorMessage, useToast } from './toast';
import { StatusChip, Tabs } from './ui';

const MAX_STAGE_HEIGHT = 640;
/** Nebeneinander: Display-Formate in Originalgröße, große Social-/Video-Formate auf diese Höhe. */
const GALLERY_MAX_HEIGHT = 600;

type Layout = 'gallery' | 'list';

export function Summary({ campaign }: { campaign: CampaignDetail }) {
  const s = campaign.stats;
  return (
    <div className="summary">
      <div>
        <strong>{s.total}</strong>
        <span>Motive</span>
      </div>
      <div>
        <strong style={{ color: s.approved ? 'var(--ok)' : undefined }}>{s.approved}</strong>
        <span>freigegeben</span>
      </div>
      <div>
        <strong style={{ color: s.changesRequested ? 'var(--danger)' : undefined }}>
          {s.changesRequested}
        </strong>
        <span>Änderungen gewünscht</span>
      </div>
      <div>
        <strong>{s.openComments}</strong>
        <span>offene Kommentare</span>
      </div>
    </div>
  );
}

function useApprove(banner: BannerCard, campaign: CampaignDetail) {
  const ensureGuest = useGuest();
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async () => {
      const guest = await ensureGuest();
      if (campaign.permissions.isGuest && !guest) return null;
      return post(`/api/banners/${banner.id}/decision`, {
        versionId: banner.current!.id,
        decision: 'approved',
        guestName: guest?.name,
        guestEmail: guest?.email,
      });
    },
    onSuccess: async (result) => {
      if (!result) return;
      toast(`„${banner.name}“ freigegeben.`);
      await queryClient.invalidateQueries({ queryKey: qk('campaign', campaign.id) });
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });
}

function BannerActions({
  banner,
  base,
  campaign,
  compact = false,
}: {
  banner: BannerCard;
  base: string;
  campaign: CampaignDetail;
  compact?: boolean;
}) {
  const approve = useApprove(banner, campaign);
  const link = `${base}/motiv/${banner.id}`;
  return (
    <div className="row-wrap">
      <Link className="btn btn-primary btn-sm btn-arrow" to={link}>
        {compact ? 'Ansehen' : campaign.permissions.canComment ? 'Ansehen & kommentieren' : 'Ansehen'}
      </Link>
      {campaign.permissions.canApprove && banner.status !== 'approved' && (
        <button
          className="btn btn-approve btn-sm"
          disabled={approve.isPending}
          onClick={() => approve.mutate()}
        >
          <Check size={16} strokeWidth={3} /> Freigeben
        </button>
      )}
    </div>
  );
}

/** Liste: ein Motiv je Zeile mit allen Angaben daneben. */
function PresentBanner({
  banner,
  base,
  campaign,
  replayKey,
}: {
  banner: BannerCard;
  base: string;
  campaign: CampaignDetail;
  replayKey: number;
}) {
  const [stageRef, size] = useElementSize<HTMLDivElement>();
  const version = banner.current;
  if (!version) return null;
  const { w, h } = sizeOf(version);
  const scale = size.width ? Math.min(1, size.width / w, MAX_STAGE_HEIGHT / h) : 0;
  return (
    <article className="present-banner">
      <div className="present-stage" ref={stageRef}>
        {scale > 0 && <BannerFrame version={version} scale={scale} replayKey={replayKey} />}
      </div>
      <div className="present-info">
        <div className="row-wrap">
          <StatusChip status={banner.status} />
          <span className="chip chip-outline">{KIND_LABELS[banner.kind]}</span>
        </div>
        <h3>{banner.name}</h3>
        <p className="small muted">
          Version {version.number} · {formatBytes(version.sizeBytes)} · {formatDate(version.createdAt)}
          {scale > 0 && scale < 1 && ` · verkleinert auf ${Math.round(scale * 100)} %`}
        </p>
        {banner.commentCount > 0 && (
          <p className="small row">
            <MessageSquare size={15} /> {pluralize(banner.commentCount, 'Kommentar', 'Kommentare')}
          </p>
        )}
        <BannerActions banner={banner} base={base} campaign={campaign} />
      </div>
    </article>
  );
}

/** Nebeneinander: mehrere Motive gleichzeitig sehen und Animationen, Inhalte und Timings vergleichen. */
function GalleryItem({
  banner,
  base,
  campaign,
  replayKey,
  maxWidth,
}: {
  banner: BannerCard;
  base: string;
  campaign: CampaignDetail;
  replayKey: number;
  maxWidth: number;
}) {
  const version = banner.current;
  if (!version) return null;
  const { w, h } = sizeOf(version);
  const scale = Math.min(1, maxWidth / w, GALLERY_MAX_HEIGHT / h);
  return (
    <article className="gallery-item" style={{ width: Math.max(Math.round(w * scale), 190) }}>
      <div className="gallery-stage">
        <BannerFrame version={version} scale={scale} replayKey={replayKey} />
      </div>
      <div className="gallery-caption">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <StatusChip status={banner.status} />
          {banner.openCommentCount > 0 && (
            <span className="status-icon" title="Offene Kommentare">
              <MessageSquare size={14} /> {banner.openCommentCount}
            </span>
          )}
        </div>
        <strong className="gallery-title" title={banner.name}>
          {banner.name}
        </strong>
        <span className="tiny muted">
          {formatName(banner.width, banner.height)}
          {banner.width ? ` · ${banner.width}×${banner.height}` : ''} · v{version.number}
          {scale < 1 && ` · ${Math.round(scale * 100)} %`}
        </span>
        <BannerActions banner={banner} base={base} campaign={campaign} compact />
      </div>
    </article>
  );
}

/** Kundensicht: Motive nebeneinander oder als Liste, gruppiert, mit Freigabe direkt am Motiv. */
export function PresentationView({ campaign, base }: { campaign: CampaignDetail; base: string }) {
  const withFiles = campaign.banners.filter((b) => b.current);
  const usesFolders = campaign.folders.length > 0 && withFiles.some((b) => b.folderId);
  const [tab, setTab] = useState<'banners' | 'comments'>('banners');
  const [groupBy, setGroupBy] = useStoredState<GroupBy>(
    `zeigbanner.groupBy.${campaign.id}`,
    usesFolders ? 'folder' : 'format',
    ['folder', 'format', 'motif'],
  );
  const [layout, setLayout] = useStoredState<Layout>('zeigbanner.layout', 'gallery', ['gallery', 'list']);
  const [hidden, setHidden] = useState<BannerKind[]>([]);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [replay, setReplay] = useState<{ all: number; groups: Record<string, number> }>({
    all: 0,
    groups: {},
  });
  const [galleryRef, gallerySize] = useElementSize<HTMLDivElement>();

  const groups = groupBanners(filterBanners(withFiles, hidden, status), campaign.folders, groupBy);
  const replayGroup = (key: string) =>
    setReplay((r) => ({ ...r, groups: { ...r.groups, [key]: (r.groups[key] ?? 0) + 1 } }));
  const openComments = campaign.stats.openComments;

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-text">
          <h1 className="page-title">{campaign.name}</h1>
          {campaign.description && (
            <p className="lead" style={{ whiteSpace: 'pre-wrap' }}>
              {campaign.description}
            </p>
          )}
          {campaign.dueDate && (
            <p className="small muted">Bitte um Rückmeldung bis {formatDate(campaign.dueDate)}.</p>
          )}
        </div>
        {campaign.permissions.canDownload && campaign.banners.length > 0 && (
          <div className="page-actions">
            {campaign.stats.approved > 0 && (
              <a
                className="btn btn-outline"
                href={withShare(`/api/campaigns/${campaign.id}/download?only=approved`)}
              >
                <Download size={17} /> Freigegebene (ZIP)
              </a>
            )}
            <a className="btn btn-ghost" href={withShare(`/api/campaigns/${campaign.id}/download`)}>
              Alle herunterladen
            </a>
          </div>
        )}
      </div>
      <Summary campaign={campaign} />

      <div style={{ marginTop: 28 }}>
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { value: 'banners', label: `Motive (${withFiles.length})` },
            { value: 'comments', label: openComments ? `Kommentare (${openComments} offen)` : 'Kommentare' },
          ]}
        />
      </div>

      {tab === 'comments' && (
        <div style={{ marginTop: 20 }}>
          <CommentsOverview campaign={campaign} base={base} />
        </div>
      )}

      {tab === 'banners' && (
        <>
          {withFiles.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <CollectionBar
                banners={withFiles}
                groupBy={groupBy}
                onGroupBy={setGroupBy}
                hidden={hidden}
                onHidden={setHidden}
                status={status}
                onStatus={setStatus}
              >
                <div className="segmented" role="group" aria-label="Darstellung">
                  <button
                    aria-pressed={layout === 'gallery'}
                    onClick={() => setLayout('gallery')}
                    title="Nebeneinander"
                  >
                    <LayoutGrid size={15} /> Nebeneinander
                  </button>
                  <button aria-pressed={layout === 'list'} onClick={() => setLayout('list')} title="Liste">
                    <Rows3 size={15} /> Liste
                  </button>
                </div>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setReplay((r) => ({ ...r, all: r.all + 1 }))}
                  title="Alle Animationen gleichzeitig neu starten"
                >
                  <RotateCcw size={15} /> Alle neu abspielen
                </button>
              </CollectionBar>
            </div>
          )}
          {groups.length === 0 && (
            <p className="muted" style={{ marginTop: 32 }}>
              {withFiles.length === 0 ? 'Noch keine Motive vorhanden.' : 'Keine Motive für diese Auswahl.'}
            </p>
          )}
          <div ref={galleryRef}>
            {groups.map((group) => {
              const replayKey = replay.all + (replay.groups[group.key] ?? 0);
              return (
                <section key={group.key} className="section">
                  <div className="section-head">
                    <h2 className="section-title">{group.title}</h2>
                    {group.subtitle && <span className="small muted">{group.subtitle}</span>}
                    <span className="spacer" />
                    <span className="small muted">{pluralize(group.banners.length, 'Motiv', 'Motive')}</span>
                    {group.banners.some((b) => b.kind !== 'image') && (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => replayGroup(group.key)}
                        title="Diese Gruppe gleichzeitig neu starten"
                      >
                        <RotateCcw size={14} /> Neu abspielen
                      </button>
                    )}
                  </div>
                  {layout === 'gallery' ? (
                    <div className="present-gallery">
                      {gallerySize.width > 0 &&
                        group.banners.map((banner) => (
                          <GalleryItem
                            key={banner.id}
                            banner={banner}
                            base={base}
                            campaign={campaign}
                            replayKey={replayKey}
                            maxWidth={gallerySize.width}
                          />
                        ))}
                    </div>
                  ) : (
                    group.banners.map((banner) => (
                      <PresentBanner
                        key={banner.id}
                        banner={banner}
                        base={base}
                        campaign={campaign}
                        replayKey={replayKey}
                      />
                    ))
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
